import { useEffect, useCallback, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Country, Utility } from '../model/bill-settings/bill-settings';
import { RootState } from '../stores/store';
import { setCountrySelectorVisibility, setExportButtonVisibility, setSelectedCountry, setSelectedUtility, setBreadCrumbButtonVisibility, bumpPageTitleVersion, setBroadcastButtonVisibility } from '../stores/slices/bills-layout-slice';
import { layoutHandlerService } from '../pages/admin/bills/service/LayoutHandlerService';

interface UseConfigLayoutOptions {
  showCountrySelector?: boolean;
  showExportButton?: boolean;
  exportHandler?: (() => void) | null;
  goBackHandler?: (() => void) | null;
  country?: Country | null;
  utility?: Utility | null;
  showBreadCrumb?: boolean;
  pageTitle?: ReactNode;
  showBroadcastButton?: boolean;
  broadcastHandler?: (() => void) | null;
}

export const useConfigLayout = (options: Partial<UseConfigLayoutOptions> = {}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentState = useSelector((state: RootState) => state.billsLayoutConfig);

  const defaultGoBack = () => navigate(-1);
  
  const updateLayout = useCallback((newOptions: Partial<UseConfigLayoutOptions>) => {
    if (newOptions.showCountrySelector !== undefined) {
      dispatch(setCountrySelectorVisibility(newOptions.showCountrySelector));
    }
    
    if (newOptions.showExportButton !== undefined) {
      dispatch(setExportButtonVisibility(newOptions.showExportButton));
    }
    
    if (newOptions.exportHandler !== undefined) {
      layoutHandlerService.setExportHandler(newOptions.exportHandler);
    }

    if (newOptions.goBackHandler !== undefined) {
        layoutHandlerService.setGoBackHandler(newOptions.goBackHandler);
      }
    
    if (newOptions.country !== undefined) {
      dispatch(setSelectedCountry(newOptions.country));
    }
    
    if (newOptions.utility !== undefined) {
      dispatch(setSelectedUtility(newOptions.utility));
    }

    if (newOptions.showBreadCrumb !== undefined) {
        dispatch(setBreadCrumbButtonVisibility(newOptions.showBreadCrumb));
    }

    if (newOptions.pageTitle !== undefined) {
        layoutHandlerService.setPageTitleNode(newOptions.pageTitle ?? null);
        dispatch(bumpPageTitleVersion());
    }

    if (newOptions.showBroadcastButton !== undefined) {
        dispatch(setBroadcastButtonVisibility(newOptions.showBroadcastButton));
    }

    if (newOptions.broadcastHandler !== undefined) {
        layoutHandlerService.setBroadcastHandler(newOptions.broadcastHandler);
    }
  }, [dispatch]);
  
  useEffect(() => {
    if (Object.keys(options).length > 0) {
      updateLayout(options);
    }
    
    return () => {
      if (options.exportHandler) {
        layoutHandlerService.setExportHandler(null);
      }

      if (options.goBackHandler) {
        layoutHandlerService.setGoBackHandler(null);
      }

      if (options.broadcastHandler) {
        layoutHandlerService.setBroadcastHandler(null);
      }
    };
  }, []); 
  return {
    // Current state
    selectedCountry: currentState.selectedCountry,
    selectedUtility: currentState.selectedUtility,
    isCountrySelectorVisible: currentState.isCountrySelectorVisible,
    isExportButtonVisible: currentState.isExportButtonVisible,
    isBreadCrumbVisible: currentState.isBreadCrumbVisible,
    isBroadcastButtonVisible: currentState.isBroadcastButtonVisible,
    pageTitle: currentState.pageTitleVersion >= 0 ? layoutHandlerService.getPageTitleNode() : null,
    handleExport:  () => {
        const handler = layoutHandlerService.getExportHandler();
        if (handler) handler();
      },

    handleGoBack: () => {
        const handler = layoutHandlerService.getGoBackHandler();
        if (handler) handler();
        else defaultGoBack();
      },

    handleBroadcast: () => {
        const handler = layoutHandlerService.getBroadcastHandler();
        if (handler) handler();
      },

    // Update function
    updateLayout,
  };
};
